import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const PropertyDetails = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 1,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  CommonButtons,
  onNavigateNext,
}) => {

  // DEBUG INFO
  console.log('🔍 PropertyDetails Debug:', {
    receivedFormMode: formMode,
    receivedCurrentStep: currentStep, 
    receivedLastSavedStep: lastSavedStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    shouldBeEdit: lastSavedStep >= currentStep,
    calculation: `lastSavedStep(${lastSavedStep}) >= currentStep(${currentStep}) = ${lastSavedStep >= currentStep}`
  });

  // CHILD EDIT MODE
  const isEditMode = useMemo(() => {
    const result = formMode === "edit" || lastSavedStep >= currentStep;
    console.log('🔄 Calculating isEditMode:', { 
      formMode, 
      lastSavedStep, 
      currentStep, 
      result 
    });
    return result;
  }, [formMode, lastSavedStep, currentStep]);

  // STATE
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");        
const [toastKey, setToastKey] = useState(0);
  const [touched, setTouched] = useState({});
  const [showValidation, setShowValidation] = useState(false);

  // Field visibility
  const [showApartmentName, setShowApartmentName] = useState(false);
  const [showTotalFloors, setShowTotalFloors] = useState(true);
  const [showNoOfFloors, setShowNoOfFloors] = useState(false);

  // Form values
  const [form, setForm] = useState({
    apartmentType: "",
    apartmentName: "",
    bhkType: "",
    builtUpArea: "",
    builtUpAreaUnit: "",
    floor: "",
    totalFloors: "",
    noOfFloors: "",
    propertyAge: "",
    facing: "",
    floorType: "",
  });

  // MAPPING
  const FIELD_TO_ATTRIBUTE_MAP = useMemo(
    () => ({
      apartmentType: "apartment_type",
      apartmentName: "apartment_name",
      bhkType: "bhk_type",
      floor: "floor",
      totalFloors: "total_floor",
      noOfFloors: "no_of_floors",
      propertyAge: "property_age",
      facing: "facing",
      floorType: "floor_type",
      builtUpArea: "built_up_area",
      builtUpAreaUnit: "built_up_area_unit",
    }),
    []
  );

  const normalize = useCallback(
    (s) => String(s || "").trim().toLowerCase().replace(/[\s_]/g, ""),
    []
  );

  const getOptions = useCallback(
    (fieldKey) => {
      const match = normalize(FIELD_TO_ATTRIBUTE_MAP[fieldKey] || fieldKey);

      let options = attributeValues.filter(
        (it) => it?.isActive && normalize(it.attrName) === match
      );

      // Custom Sorting for BHK / RK
      if (fieldKey === "bhkType") {
        options = [...options].sort((a, b) => {
          const parse = (label) => {
            const numMatch = label?.match(/\d+/);
            const number = numMatch ? Number(numMatch[0]) : 999;
            const isRK = label?.toLowerCase().includes("rk");
            const typePriority = isRK ? 0 : 1;
            return { number, typePriority };
          };

          const A = parse(a.label);
          const B = parse(b.label);

          if (A.number !== B.number) {
            return A.number - B.number;
          }

          return A.typePriority - B.typePriority;
        });
      }

      if (fieldKey === "propertyAge") {
        const orderMap = {
          "less than": 1,
          "1 to 3": 2,
          "3 to 5": 3,
          "5 to 10": 4,
          "more than": 5,
        };

        options = [...options].sort((a, b) => {
          const getPriority = (label) => {
            const lower = label?.toLowerCase() || "";
            if (lower.includes("less than")) return orderMap["less than"];
            if (lower.includes("1 to 3")) return orderMap["1 to 3"];
            if (lower.includes("3 to 5")) return orderMap["3 to 5"];
            if (lower.includes("5 to 10")) return orderMap["5 to 10"];
            if (lower.includes("more than")) return orderMap["more than"];
            return 999;
          };
          return getPriority(a.label) - getPriority(b.label);
        });
      }
      return options;
    },
    [attributeValues, FIELD_TO_ATTRIBUTE_MAP, normalize]
  );

  // VALIDATION RULES
  const validationRules = useMemo(
    () => ({
      apartmentType: { required: true, message: "Apartment Type is required" },
      bhkType: { required: true, message: "BHK Type is required" },
      builtUpArea: {
        required: true,
        message: "Built-up Area is required",
        custom: (v) => {
          if (!v || v === "") return "Built-up Area is required";
          if (Number(v) <= 0) return "Built-up Area must be greater than 0";
          return null;
        },
      },
      builtUpAreaUnit: {
        required: true,
        message: "Built-up Area Unit is required",
      },
      propertyAge: { required: true, message: "Property Age is required" },
    
      floor: { required: true, message: "Floor is required" },
      totalFloors: { required: true, message: "Total Floors is required" },
      noOfFloors: { required: true, message: "No. of Floors is required" },
      apartmentName: { 
        required: () => showApartmentName,
        message: "Apartment Name is required" 
      },

    }),
    [showApartmentName]
  );

  const getFieldError = useCallback(
    (field) => {
      const rule = validationRules[field];
      if (!rule) return null;

      const val = form[field];

      // Skip validation for hidden fields
      if (field === "apartmentName" && !showApartmentName) return null;
      if (field === "noOfFloors" && !showNoOfFloors) return null;
      if (field === "totalFloors" && showNoOfFloors) return null;
      if (field === "floor" && showNoOfFloors) return null;

      // Check if field should be required based on visibility
      let isRequired = rule.required;
      if (typeof rule.required === 'function') {
        isRequired = rule.required();
      }

      // Check required
      if (isRequired && (!val || val === "")) {
        return rule.message;
      }
      
      // Check custom validation
      if (rule.custom && val) {
        return rule.custom(val);
      }
      
      return null;
    },
    [form, validationRules, showApartmentName, showNoOfFloors]
  );

  const shouldShowError = useCallback((field) => {
    return showValidation || touched[field];
  }, [showValidation, touched]);

  const validateAllFields = useCallback(() => {
    const fields = Object.keys(validationRules);
    for (let field of fields) {
      // Skip validation for hidden fields
      if (field === "apartmentName" && !showApartmentName) continue;
      if (field === "noOfFloors" && !showNoOfFloors) continue;
      if (field === "totalFloors" && showNoOfFloors) continue;
      if (field === "floor" && showNoOfFloors) continue;
      
      const error = getFieldError(field);
      if (error) return false;
    }
    return true;
  }, [validationRules, getFieldError, showApartmentName, showNoOfFloors]);

  const hasErrors = useMemo(() => {
    return !validateAllFields();
  }, [validateAllFields]);

  // VISIBILITY HANDLING
  const updateFieldVisibility = useCallback(
    (apartmentTypeIdOrLabel) => {
      const options = getOptions("apartmentType");
      const selected =
        options.find((o) => String(o.id) === String(apartmentTypeIdOrLabel)) ||
        options.find(
          (o) => normalize(o.label) === normalize(apartmentTypeIdOrLabel)
        );

      const name =
        selected?.label?.toLowerCase() ||
        String(apartmentTypeIdOrLabel || "").toLowerCase();

      const isIHV =
        name.includes("independent house") || name.includes("villa");

      setShowApartmentName(
        name.includes("apartment") || name.includes("gated community villa")
      );
      setShowTotalFloors(!isIHV);
      setShowNoOfFloors(isIHV);
    },
    [getOptions, normalize]
  );

  // Update field
  const updateField = useCallback(
    (key, value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setTouched((prev) => ({ ...prev, [key]: true }));
      if (key === "apartmentType") updateFieldVisibility(value);
    },
    [updateFieldVisibility]
  );

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // HYDRATE FORM WITH EXISTING DATA
  useEffect(() => {
    if (!data || !attributeValues.length) return;

    console.log('💧 Hydrating form with data:', data);

    const attributes = data.attributes || {};
    const areaInfo = data.area_information || {};
    
    setForm(prevForm => {
      const updated = { ...prevForm };
      
      Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(([fieldKey, apiKey]) => {
        let value;

        if (
          fieldKey === "builtUpArea" ||
          fieldKey === "builtUpAreaUnit"
        ) {
          value = areaInfo[apiKey];
        } else {
          value = attributes[apiKey];
        }

        if (value !== undefined && value !== null) {
          updated[fieldKey] = value;
        }
      });

      console.log('💧 Updated form state:', updated);
      
      if (updated.apartmentType) {
        updateFieldVisibility(updated.apartmentType);
      }
      
      return updated;
    });
    
    setTouched({});
    setShowValidation(false);
    
  }, [data, attributeValues, FIELD_TO_ATTRIBUTE_MAP, updateFieldVisibility]);

  // SAVE
const handleSave = useCallback(
  async (navigateNext = false) => {
    console.log("💾 Starting save process...", {
      navigateNext,
      propertyUuid,
      currentStep,
      lastSavedStep,
      isEditMode,
    });

    setShowValidation(true);

    const allTouched = {};
    Object.keys(validationRules).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    if (hasErrors) {
      setError("Please fill all required fields");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess(""); 

    try {
      let uuid = propertyUuid;

      // ------------------------------------------------
      // 1. CREATE property only when this is ADD flow
      // ------------------------------------------------
      if (!uuid) {
        console.log("🆕 Creating new property...");

        uuid = await createProperty(form);

        console.log("✅ Property created with UUID:", uuid);
      } else {
        console.log("📝 Managing existing property:", uuid);
      }

      // ------------------------------------------------
      // 2. Build property detail attributes
      // ------------------------------------------------
      const attributes = {};

      Object.entries(FIELD_TO_ATTRIBUTE_MAP).forEach(
        ([key, apiKey]) => {
          if (key === "apartmentName" && !showApartmentName) return;
          if (key === "noOfFloors" && !showNoOfFloors) return;
          if (key === "totalFloors" && showNoOfFloors) return;
          if (key === "floor" && showNoOfFloors) return;

          const val = form[key];

          if (
            val !== "" &&
            val !== null &&
            val !== undefined
          ) {
            attributes[apiKey] = isNaN(val)
              ? val
              : Number(val);
          }
        }
      );

      console.log("💾 Saving attributes:", attributes);

      // ------------------------------------------------
      // 3. Save property_detail_attribute
      // ------------------------------------------------
      const saveResult = await saveSectionAttributes(
        uuid,
        attributes,
        form,
        "property_details"
      );

      console.log(
        "✅ property_detail_attribute response:",
        saveResult
      );

      // ------------------------------------------------
      // 4. Get PROPERTY_ID from the API response
      // ------------------------------------------------
      const returnedPropertyId =
        saveResult?.response?.data?.property_id ||
        saveResult?.response?.data?.PROPERTY_ID ||
        data?.property_id ||
        data?.PROPERTY_ID ||
        data?.id ||
        null;

      console.log("✅ PROPERTY_ID:", returnedPropertyId);

      // ------------------------------------------------
      // 5. Notify parent for both ADD and EDIT
      // ------------------------------------------------
      onSaveSuccess?.(
        uuid,
        form,
        currentStep,
        returnedPropertyId
      );

      console.log(
        "✅ onSaveSuccess called:",
        {
          uuid,
          currentStep,
          returnedPropertyId,
        }
      );

      // ------------------------------------------------
      // 6. Complete current step
      // ------------------------------------------------
      onComplete?.(form);

       setSuccess("Property details saved successfully!");
  setToastKey((k) => k + 1);

      // ------------------------------------------------
      // 7. Navigate to next step
      // ------------------------------------------------
      if (navigateNext) {
        console.log("➡️ Navigating to next step...");

        onSaved?.(uuid, form);
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error("❌ Save failed:", err);

      setError(
        "Failed to save property details"
      );
    } finally {
      setSaving(false);
    }
  },
  [
    form,
    hasErrors,
    validationRules,
    propertyUuid,
    createProperty,
    saveSectionAttributes,
    onSaveSuccess,
    onComplete,
    onSaved,
    onNavigateNext,
    showApartmentName,
    showNoOfFloors,
    FIELD_TO_ATTRIBUTE_MAP,
    currentStep,
    lastSavedStep,
    isEditMode,
    data,
  ]
);

  const commonFields = CommonFields({
    form,
    touched,
    getFieldError: (field) => shouldShowError(field) ? getFieldError(field) : null,
    handleBlur,
    updateField,
    getOptions,
  });

  const { renderDropdown, renderNumberField, renderTextInput } = commonFields;

  return (
    <>
      {error && (
        <ToastNotification
        key={`err-${toastKey}`}   
          kind="error"
          title="Validation Error"
          subtitle={error}
          lowContrast
          timeout={5000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

{/* 👇 ADD this whole block */}
{success && (
  <ToastNotification
    key={`ok-${toastKey}`}                        // 👈 IMPORTANT: forces remount
    kind="success"
    title="Success"
    subtitle={success}
    lowContrast
    timeout={3000}
    onClose={() => setSuccess("")}
    style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
  />
)}
      <Grid>
        <Column sm={4} md={8} lg={16}>
          <Tile style={{ backgroundColor: "white" }}>
            <Stack gap="1rem">
              {/* APARTMENT TYPE */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>
                    {renderDropdown("apartmentType", "Resident Type")}
                  </Column>
                  <Column sm={4} md={4} lg={8}>
                    {showApartmentName && (
                      renderTextInput("apartmentName", "Apartment Name", "Enter apartment or community name", true)
                    )}
                  </Column>
                </Grid>
              </FormGroup>

              {/* BHK TYPE */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>
                    {renderDropdown("bhkType", "BHK Type")}
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUILT-UP AREA + UNIT */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>
                    <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end" }}>
                      <div style={{ flex: 2 }}>
                        {renderNumberField("builtUpArea", "Built-up Area", "0", true)}
                      </div>
                      <div style={{ flex: 1 }}>
                        {renderDropdown("builtUpAreaUnit", "Unit", true)}
                      </div>
                    </div>
                  </Column>
                </Grid>
              </FormGroup>

              {/* AGE + FACING */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={8}>{renderDropdown("propertyAge", "Property Age")}</Column>
                  <Column sm={4} md={4} lg={8}>{renderDropdown("facing", "Facing", false)}</Column>
                </Grid>
              </FormGroup>

              {/* FLOOR */}
              <FormGroup>
                <Grid>
                  <Column sm={4} md={4} lg={5}>{renderDropdown("floorType", "Floor Type",false)}</Column>

                  {showNoOfFloors ? (
                    <Column sm={4} md={4} lg={5}>{renderDropdown("noOfFloors", "No. of Floors")}</Column>
                  ) : (
                    <>
                      <Column sm={4} md={4} lg={5}>{renderDropdown("floor", "Floor")}</Column>
                      {showTotalFloors && (
                        <Column sm={4} md={4}  lg={5}>{renderDropdown("totalFloors", "Total Floors")}</Column>
                      )}
                    </>
                  )}
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={() => handleSave(false)}   
                  onNext={() => handleSave(true)}     
                  showSave
                  showNext
                  isSaveLoading={saving}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </>
  );
};

export default PropertyDetails;